<template>
  <v-dialog
      v-model="dialog"
      max-width="1100"
      scrollable
      :persistent="uploading"
  >
    <v-card v-if="schematron">
      <v-card-title>
        {{ schematron.name }}
        <span class="ml-2 subtitle-2 grey--text text--darken-1">
          {{ $t('schematrons.versions') }}
        </span>
      </v-card-title>

      <v-card-text>
        <v-alert
            v-if="error"
            type="error"
            dense
            text
        >
          {{ error }}
        </v-alert>

        <v-data-table
            class="versions-table"
            :headers="headers"
            :items="versions"
            :items-per-page="-1"
            :loading="loading"
            hide-default-footer
        >
          <template v-slot:item.versionNumber="{ item }">
            v{{ item.versionNumber }}
            <v-chip
                v-if="item.id === schematron.currentVersionId"
                color="success"
                x-small
                class="ml-1"
            >
              published
            </v-chip>
          </template>

          <template v-slot:item.sourceFileName="{ item }">
            {{ item.sourceFileName }}
            <p class="mb-0">
              <small class="grey--text text--darken-1">
                {{ item.sourceSize | bytes }} → {{ item.xslSize | bytes }}
              </small>
            </p>
          </template>

          <template v-slot:item.releasedAt="{ item }">
            {{ item.releasedAt | date }}
          </template>

          <template v-slot:item.createdAt="{ item }">
            {{ item.createdAt | date }}
          </template>

          <!-- Notes run to 500 characters; left unbounded one of them stretches the table
               and squeezes every other column. -->
          <template v-slot:item.notes="{ item }">
            <div
                class="notes-cell"
                :title="item.notes"
            >
              {{ item.notes }}
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
                v-if="item.id !== schematron.currentVersionId"
                icon
                title="Publish"
                :disabled="uploading"
                @click="publish(item)"
            >
              <v-icon small>radio_button_unchecked</v-icon>
            </v-btn>
            <v-btn
                icon
                title="Download source"
                :disabled="uploading"
                @click="download(item)"
            >
              <v-icon small>download</v-icon>
            </v-btn>
            <v-btn
                v-if="item.id !== schematron.currentVersionId"
                icon
                title="Delete"
                :disabled="uploading"
                @click="remove(item)"
            >
              <v-icon small>delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <v-divider class="my-4" />

        <h4 class="subtitle-1 mb-2">{{ $t('schematrons.uploadVersion') }}</h4>
        <v-row align="center">
          <v-col cols="5">
            <v-file-input
                v-model="file"
                label="Schematron file (.sch)"
                accept=".sch,.xml"
                prepend-icon=""
                prepend-inner-icon="description"
                dense
                hide-details
                show-size
            />
          </v-col>
          <v-col cols="3">
            <date-picker
                :date.sync="releasedAt"
                label="Released at"
            />
          </v-col>
          <v-col cols="4">
            <v-text-field
                v-model="notes"
                label="Notes"
                maxlength="500"
                dense
                hide-details
            />
          </v-col>
        </v-row>
        <p
            v-if="uploading"
            class="caption grey--text text--darken-1 mt-2 mb-0"
        >
          {{ $t('schematrons.compiling') }}
        </p>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
            text
            :disabled="uploading"
            @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
            color="primary"
            :disabled="!file"
            :loading="uploading"
            @click="upload"
        >
          Upload
        </v-btn>
      </v-card-actions>
    </v-card>

    <confirmation ref="confirmationPopup" />
  </v-dialog>
</template>
<script>
import schematronsApi from '@/api/schematrons';
import Confirmation from '@/components/Confirmation';
import DatePicker from '@/components/DatePicker';

export default {
  name: 'VersionsDialog',
  components: { Confirmation, DatePicker },

  filters: {
    date(value) {
      return value ? value.substring(0, 10) : '';
    },
    bytes(value) {
      if (!value) {
        return '0 KB';
      }
      return `${Math.round(value / 1024)} KB`;
    },
  },

  data() {
    return {
      dialog: false,
      loading: false,
      uploading: false,
      error: null,
      schematron: null,
      versions: [],
      file: null,
      releasedAt: null,
      notes: null,
      // Only the two columns that need holding down get a width: notes because free text
      // stretches without one, actions because three icon buttons must stay on one line.
      // Pinning the rest is what pushed the table past the dialog and wrapped File.
      headers: [
        { text: 'Version', value: 'versionNumber' },
        { text: 'File', value: 'sourceFileName' },
        { text: 'Released', value: 'releasedAt' },
        { text: 'Uploaded', value: 'createdAt' },
        { text: 'Notes', value: 'notes', width: 240 },
        {
          text: '', value: 'actions', sortable: false, align: 'end', width: 130,
        },
      ],
    };
  },

  methods: {
    async open(schematron) {
      this.schematron = schematron;
      this.error = null;
      this.file = null;
      this.releasedAt = null;
      this.notes = null;
      this.dialog = true;
      await this.refresh();
    },

    async refresh() {
      this.loading = true;
      try {
        const data = await schematronsApi.getSchematron(this.schematron.id);
        this.schematron = data.data;
        this.versions = this.schematron.versions || [];
      } finally {
        this.loading = false;
      }
    },

    async upload() {
      this.error = null;
      this.uploading = true;
      try {
        await schematronsApi.createVersion(
          this.schematron.id,
          {
            releasedAt: this.releasedAt || new Date().toISOString().substring(0, 10),
            notes: this.notes,
          },
          this.file,
        );
        this.file = null;
        this.notes = null;
        await this.refresh();
        this.$emit('changed');
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message)
          || 'Could not upload the version.';
      } finally {
        this.uploading = false;
      }
    },

    publish(version) {
      this.$refs.confirmationPopup.showConfirm(
        this.$t('schematrons.publishConfirmationHeading'),
        this.$t('schematrons.publishConfirmationText'),
        async () => {
          await schematronsApi.publishVersion(version.id);
          await this.refresh();
          this.$emit('changed');
        },
      );
    },

    remove(version) {
      this.$refs.confirmationPopup.showConfirm(
        this.$t('schematrons.deleteVersionConfirmationHeading'),
        `v${version.versionNumber} - ${version.sourceFileName}`,
        async () => {
          this.error = null;
          try {
            await schematronsApi.deleteVersion(version.id);
            await this.refresh();
            this.$emit('changed');
          } catch (e) {
            this.error = (e.response && e.response.data && e.response.data.message)
              || 'Could not delete the version.';
          }
        },
      );
    },

    async download(version) {
      const blob = await schematronsApi.downloadVersionSource(version.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = version.sourceFileName;
      link.click();
      window.URL.revokeObjectURL(url);
    },
  },
};
</script>
<style scoped>
/* Dates and version numbers are short; letting them wrap only makes rows uneven. */
.versions-table ::v-deep th,
.versions-table ::v-deep td {
  white-space: nowrap;
}

.notes-cell {
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
